import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2, X, LayoutList, LayoutGrid, Calculator, Search } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils';
import api from '@/lib/api';
import { HelpTip } from '@/components/HelpTip';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [marketplaces, setMarketplaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('cards');
  const [calcCost, setCalcCost] = useState('');
  const [calcPrice, setCalcPrice] = useState('');
  const [calcMpId, setCalcMpId] = useState('');
  const [formData, setFormData] = useState({
    name: '', product_type: '', cost: '', price: '', sku: '', product_category_id: '',
    marketplace_prices: [],
  });

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCategory) params.product_category_id = filterCategory;
      const response = await api.get('/products', { params });
      setProducts(response.data);
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  }, [filterCategory]);

  const loadProductCategories = useCallback(async () => {
    try {
      const response = await api.get('/product-categories');
      setProductCategories(response.data);
    } catch (error) {
      toast.error('Erro ao carregar categorias');
    }
  }, []);

  const loadMarketplaces = useCallback(async () => {
    try {
      const response = await api.get('/marketplaces');
      setMarketplaces(response.data);
    } catch (error) {
      toast.error('Erro ao carregar marketplaces');
    }
  }, []);

  useEffect(() => {
    loadProducts();
    loadProductCategories();
    loadMarketplaces();
  }, [loadProducts, loadProductCategories, loadMarketplaces]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        product_type: formData.product_type,
        cost: parseFloat(formData.cost) || 0,
        price: parseFloat(formData.price) || 0,
        sku: formData.sku,
        product_category_id: formData.product_category_id || null,
        marketplace_prices: formData.marketplace_prices
          .filter(mp => mp.marketplace_id && mp.price)
          .map(mp => ({ marketplace_id: mp.marketplace_id, price: parseFloat(mp.price) })),
      };
      if (editingProduct) {
        await api.put(`/products/${editingProduct.id}`, payload);
        toast.success('Produto atualizado!');
      } else {
        await api.post('/products', payload);
        toast.success('Produto criado!');
      }
      setIsDialogOpen(false);
      resetForm();
      loadProducts();
    } catch (error) {
      toast.error('Erro ao salvar produto');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este produto?')) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success('Produto excluído!');
      loadProducts();
    } catch (error) {
      toast.error('Erro ao excluir produto');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', product_type: '', cost: '', price: '', sku: '', product_category_id: '', marketplace_prices: [] });
    setEditingProduct(null);
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      product_type: product.product_type || '',
      cost: product.cost?.toString() || '',
      price: product.price?.toString() || '',
      sku: product.sku || '',
      product_category_id: product.product_category_id || '',
      marketplace_prices: (product.marketplace_prices || []).map(mp => ({
        marketplace_id: mp.marketplace_id,
        price: mp.price?.toString() || '',
      })),
    });
    setIsDialogOpen(true);
  };

  const addMarketplacePrice = () => {
    const usedIds = formData.marketplace_prices.map(mp => mp.marketplace_id);
    const available = marketplaces.filter(mp => !usedIds.includes(mp.id));
    if (available.length === 0) {
      toast.info('Todos os marketplaces já foram adicionados');
      return;
    }
    setFormData({
      ...formData,
      marketplace_prices: [...formData.marketplace_prices, { marketplace_id: available[0].id, price: formData.price || '' }],
    });
  };

  const removeMarketplacePrice = (index) => {
    setFormData({
      ...formData,
      marketplace_prices: formData.marketplace_prices.filter((_, i) => i !== index),
    });
  };

  const updateMarketplacePrice = (index, field, value) => {
    const updated = [...formData.marketplace_prices];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, marketplace_prices: updated });
  };

  const getCategoryName = (categoryId) => productCategories.find(c => c.id === categoryId)?.name || '';

  const mpMap = useMemo(() => {
    const m = {};
    marketplaces.forEach(mp => { m[mp.id] = mp; });
    return m;
  }, [marketplaces]);

  const calcFeesFromRules = (sellPrice, parsedRules) => {
    if (!parsedRules || parsedRules.length === 0) return 0;
    let totalComissao = 0;
    let totalTaxa = 0;
    let matchedFaixa = false;
    for (const rule of parsedRules) {
      if (rule.tipo === 'faixa' && !matchedFaixa) {
        const minV = rule.min_valor || 0;
        const maxV = rule.max_valor;
        let inRange = false;
        if (maxV !== null && maxV !== undefined) {
          inRange = sellPrice >= minV && sellPrice <= maxV;
        } else {
          inRange = sellPrice >= minV;
        }
        if (inRange) {
          totalComissao += rule.comissao_percent || 0;
          totalTaxa += rule.taxa_fixa || 0;
          matchedFaixa = true;
        }
      } else if (rule.tipo === 'adicional') {
        totalComissao += rule.comissao_percent || 0;
        totalTaxa += rule.taxa_fixa || 0;
      }
    }
    return (sellPrice * totalComissao / 100) + totalTaxa;
  };

  const calcProfit = (sellPrice, cost, parsedRules) => {
    const p = sellPrice || 0;
    const c = cost || 0;
    const fee = calcFeesFromRules(p, parsedRules);
    return round2(p - c - fee);
  };

  const calcMargin = (profit, sellPrice) => {
    if (!sellPrice || sellPrice <= 0) return 0;
    return round2((profit / sellPrice) * 100);
  };

  const round2 = (n) => Math.round(n * 100) / 100;

  const calcDetailedFeesFromRules = useCallback((sellPrice, parsedRules) => {
    if (!parsedRules || parsedRules.length === 0) return { items: [], totalFee: 0, matchedRuleNames: [] };
    const items = [];
    const matchedRuleNames = [];
    let matchedFaixa = false;
    for (const rule of parsedRules) {
      let applies = false;
      if (rule.tipo === 'faixa' && !matchedFaixa) {
        const minV = rule.min_valor || 0;
        const maxV = rule.max_valor;
        applies = maxV != null ? (sellPrice >= minV && sellPrice <= maxV) : (sellPrice >= minV);
        if (applies) matchedFaixa = true;
      } else if (rule.tipo === 'adicional') {
        applies = true;
      }
      if (applies) {
        matchedRuleNames.push(rule.nome);
        if (rule.comissao_percent > 0) {
          items.push({
            label: rule.tipo === 'faixa' ? `Comissão (${rule.comissao_percent}%)` : `${rule.nome} (${rule.comissao_percent}%)`,
            value: round2(sellPrice * rule.comissao_percent / 100),
          });
        }
        if (rule.taxa_fixa > 0) {
          items.push({
            label: rule.tipo === 'faixa' ? 'Taxa Fixa' : `${rule.nome} (fixa)`,
            value: round2(rule.taxa_fixa),
          });
        }
      }
    }
    return { items, totalFee: round2(items.reduce((a, i) => a + i.value, 0)), matchedRuleNames };
  }, []);

  const calcResult = useMemo(() => {
    const cost = parseFloat(calcCost) || 0;
    const price = parseFloat(calcPrice) || 0;
    if (price <= 0 || !calcMpId) return null;
    const mp = marketplaces.find(m => m.id === calcMpId);
    if (!mp) return null;
    const detailed = calcDetailedFeesFromRules(price, mp.regras_parsed || []);
    const profit = round2(price - cost - detailed.totalFee);
    const margin = price > 0 ? round2((profit / price) * 100) : 0;
    return {
      price, cost, mpName: mp.name, mpColor: mp.color,
      items: detailed.items, totalFee: detailed.totalFee,
      matchedRuleNames: detailed.matchedRuleNames,
      profit, margin, hasRules: (mp.regras_parsed || []).length > 0,
    };
  }, [calcCost, calcPrice, calcMpId, marketplaces, calcDetailedFeesFromRules]);

  const getProductMarketplaceData = (product) => {
    return marketplaces.map(mp => {
      const customPrice = (product.marketplace_prices || []).find(p => p.marketplace_id === mp.id);
      const sellPrice = customPrice ? customPrice.price : (product.price || 0);
      const profit = calcProfit(sellPrice, product.cost, mp.regras_parsed || []);
      const margin = calcMargin(profit, sellPrice);
      return { ...mp, sellPrice, profit, margin, hasCustomPrice: !!customPrice };
    });
  };

  const normalize = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return products;
    const q = normalize(searchQuery);
    return products.filter(p => {
      const catName = getCategoryName(p.product_category_id);
      const fields = [p.name, p.product_type, p.sku, catName, p.description];
      return fields.some(f => normalize(f).includes(q));
    });
  }, [products, searchQuery, productCategories]);

  return (
    <div data-testid="products-page">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-4xl font-bold text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Produtos
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground">
          Gerencie produtos e veja performance por marketplace
          <HelpTip text="Cadastre seus produtos, defina preços por marketplace e veja automaticamente o lucro e a margem de cada um." className="ml-1" />
        </p>
      </div>

      {/* Price Calculator */}
      <Card className="mb-6 overflow-hidden border-0 shadow-lg bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-700 dark:to-violet-700 text-white" data-testid="price-calculator">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
              <Calculator size={18} className="text-white" />
            </div>
            <div>
              <CardTitle className="text-base text-white">Calculadora de Preço</CardTitle>
              <p className="text-xs text-white/70 mt-0.5">
                Simule rapidamente o lucro de um produto antes de cadastrá-lo.
                <HelpTip text="Informe o custo, preço de venda e escolha o marketplace. O sistema calcula automaticamente as taxas, o lucro e a margem usando as regras cadastradas no marketplace." className="ml-1" />
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-white/80">Custo do Produto (R$)</Label>
              <Input
                type="number" step="0.01" data-testid="calc-cost"
                value={calcCost} onChange={e => setCalcCost(e.target.value)}
                placeholder="Ex: 18,90"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus-visible:ring-white/30"
              />
            </div>
            <div>
              <Label className="text-xs text-white/80">Preço de Venda (R$)</Label>
              <Input
                type="number" step="0.01" data-testid="calc-price"
                value={calcPrice} onChange={e => setCalcPrice(e.target.value)}
                placeholder="Ex: 39,90"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus-visible:ring-white/30"
              />
            </div>
            <div>
              <Label className="text-xs text-white/80">Marketplace</Label>
              <Select value={calcMpId || 'none'} onValueChange={v => setCalcMpId(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="calc-marketplace" className="bg-white/10 border-white/20 text-white [&>svg]:text-white/60"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Selecione</SelectItem>
                  {marketplaces.map(mp => (
                    <SelectItem key={mp.id} value={mp.id}>
                      <span className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: mp.color }} />
                        {mp.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Clear button - shown when any field has value */}
          {(calcCost || calcPrice || calcMpId) && (
            <div className="flex justify-end mt-3">
              <Button
                type="button" variant="ghost" size="sm"
                data-testid="calc-clear-button"
                onClick={() => { setCalcCost(''); setCalcPrice(''); setCalcMpId(''); }}
                className="text-white/70 hover:text-white hover:bg-white/10 text-xs h-8"
              >
                <X size={14} className="mr-1" />
                Limpar
              </Button>
            </div>
          )}

          {/* Result */}
          {calcResult && (
            <div className="mt-4 pt-4 border-t border-white/20" data-testid="calc-result">
              <h3 className="text-sm font-semibold text-white mb-3">Resultado da Simulação</h3>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <div className="p-3 rounded-lg bg-white/10">
                  <p className="text-xs text-white/60">Preço de Venda</p>
                  <p className="text-base font-bold text-white" data-testid="calc-result-price">{formatCurrency(calcResult.price)}</p>
                </div>
                <div className="p-3 rounded-lg bg-white/10">
                  <p className="text-xs text-white/60">Custo do Produto</p>
                  <p className="text-base font-bold text-white" data-testid="calc-result-cost">{formatCurrency(calcResult.cost)}</p>
                </div>
                <div className="p-3 rounded-lg bg-white/10">
                  <p className="text-xs text-white/60">Marketplace</p>
                  <p className="text-base font-bold text-white flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: calcResult.mpColor }} />
                    {calcResult.mpName}
                  </p>
                </div>
              </div>

              {/* Fee Breakdown */}
              {calcResult.items.length > 0 ? (
                <div className="rounded-lg border border-white/20 overflow-hidden mb-4">
                  <div className="bg-white/10 px-3 py-2">
                    <p className="text-xs font-semibold text-white/70 uppercase tracking-wide">Taxas Aplicadas</p>
                  </div>
                  <div className="divide-y divide-white/10">
                    {calcResult.items.map((item, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span className="text-white/70">{item.label}</span>
                        <span className="font-medium text-white">{formatCurrency(item.value)}</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between px-3 py-2.5 bg-white/10 text-sm font-semibold">
                      <span className="text-white">Total de Taxas</span>
                      <span className="text-rose-300" data-testid="calc-result-total-fees">{formatCurrency(calcResult.totalFee)}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-white/20 p-3 mb-4 text-sm text-white/60 italic">
                  Nenhuma regra de taxa configurada neste marketplace.
                </div>
              )}

              {/* Profit & Margin */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className={`p-3 rounded-lg ${calcResult.profit >= 0 ? 'bg-emerald-500/20 border border-emerald-400/30' : 'bg-rose-500/20 border border-rose-400/30'}`}>
                  <p className="text-xs text-white/60">Lucro Final</p>
                  <p className={`text-xl font-bold ${calcResult.profit >= 0 ? 'text-emerald-300' : 'text-rose-300'}`} data-testid="calc-result-profit">
                    {formatCurrency(calcResult.profit)}
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${calcResult.margin >= 0 ? 'bg-emerald-500/20 border border-emerald-400/30' : 'bg-rose-500/20 border border-rose-400/30'}`}>
                  <p className="text-xs text-white/60">Margem de Lucro</p>
                  <p className={`text-xl font-bold ${calcResult.margin >= 0 ? 'text-emerald-300' : 'text-rose-300'}`} data-testid="calc-result-margin">
                    {calcResult.margin.toFixed(1)}%
                  </p>
                </div>
              </div>

              {/* Explanation */}
              {calcResult.items.length > 0 && (
                <div className="rounded-lg bg-white/10 p-3 text-xs text-white/70 leading-relaxed" data-testid="calc-explanation">
                  <p className="mb-1">
                    O sistema encontrou as seguintes regras no marketplace <b className="text-white">{calcResult.mpName}</b>:
                  </p>
                  <ul className="list-disc list-inside space-y-0.5 mb-1">
                    {calcResult.items.map((item, i) => (
                      <li key={i}>{item.label}: <b className="text-white">{formatCurrency(item.value)}</b></li>
                    ))}
                  </ul>
                  <p>Todas essas regras foram aplicadas sobre o preço informado de {formatCurrency(calcResult.price)}.</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filters + Actions */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-6">
        <Select value={filterCategory || 'all'} onValueChange={val => setFilterCategory(val === 'all' ? '' : val)}>
          <SelectTrigger className="w-[180px]" data-testid="filter-category">
            <SelectValue placeholder="Todas categorias" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas categorias</SelectItem>
            {productCategories.map(cat => (
              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="product-search"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Buscar produto..."
            className="pl-8 w-[180px] sm:w-[220px] h-9"
          />
        </div>

        <div className="flex gap-1 border border-border rounded-lg p-1">
          <button onClick={() => setViewMode('list')} data-testid="view-list"
            className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-indigo-600 text-white' : 'text-muted-foreground hover:bg-accent'}`}>
            <LayoutList size={18} />
          </button>
          <button onClick={() => setViewMode('cards')} data-testid="view-cards"
            className={`p-1.5 rounded ${viewMode === 'cards' ? 'bg-indigo-600 text-white' : 'text-muted-foreground hover:bg-accent'}`}>
            <LayoutGrid size={18} />
          </button>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-product-button" className="bg-indigo-600 hover:bg-indigo-700 ml-auto">
              <Plus size={18} className="mr-2" />Novo Produto
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
            <DialogHeader>
              <DialogTitle>{editingProduct ? 'Editar' : 'Novo'} Produto</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Nome do Produto</Label>
                <Input data-testid="product-name" value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Ex: Camiseta Oversized" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tipo</Label>
                  <Input data-testid="product-type" value={formData.product_type}
                    onChange={e => setFormData({ ...formData, product_type: e.target.value })} placeholder="Ex: Camiseta" />
                </div>
                <div>
                  <Label>SKU</Label>
                  <Input data-testid="product-sku" value={formData.sku}
                    onChange={e => setFormData({ ...formData, sku: e.target.value })} placeholder="Ex: CAM-001" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Custo (R$)</Label>
                  <Input type="number" step="0.01" data-testid="product-cost" value={formData.cost}
                    onChange={e => setFormData({ ...formData, cost: e.target.value })} placeholder="0,00" />
                </div>
                <div>
                  <Label>Preço de Venda Padrão (R$)</Label>
                  <Input type="number" step="0.01" data-testid="product-price" value={formData.price}
                    onChange={e => setFormData({ ...formData, price: e.target.value })} placeholder="0,00" />
                </div>
              </div>
              <div>
                <Label>Categoria</Label>
                <Select value={formData.product_category_id || 'none'} onValueChange={val => setFormData({ ...formData, product_category_id: val === 'none' ? '' : val })}>
                  <SelectTrigger data-testid="product-category"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem categoria</SelectItem>
                    {productCategories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Marketplace Prices */}
              <div className="border-t border-border pt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold">Preços por Marketplace</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addMarketplacePrice} data-testid="add-mp-price">
                    <Plus size={14} className="mr-1" />Adicionar
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Se não definir, o preço padrão será usado para todos os marketplaces.
                </p>
                {formData.marketplace_prices.map((mpPrice, idx) => (
                  <div key={idx} className="flex items-center gap-2 mb-2">
                    <Select value={mpPrice.marketplace_id} onValueChange={val => updateMarketplacePrice(idx, 'marketplace_id', val)}>
                      <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {marketplaces.map(mp => (
                          <SelectItem key={mp.id} value={mp.id}>{mp.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input type="number" step="0.01" className="w-28" placeholder="Preço"
                      value={mpPrice.price} onChange={e => updateMarketplacePrice(idx, 'price', e.target.value)} />
                    <button type="button" onClick={() => removeMarketplacePrice(idx)}
                      className="p-1.5 text-rose-500 hover:bg-rose-50 rounded">
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>

              <Button type="submit" data-testid="product-submit" className="w-full bg-indigo-600 hover:bg-indigo-700">
                {editingProduct ? 'Atualizar' : 'Criar'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Product Display */}
      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Carregando...</div>
      ) : filteredProducts.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          {searchQuery ? 'Nenhum produto encontrado para esta busca' : 'Nenhum produto cadastrado'}
        </div>
      ) : viewMode === 'cards' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {filteredProducts.map(product => {
            const mpData = getProductMarketplaceData(product);
            const catName = getCategoryName(product.product_category_id);
            return (
              <Card key={product.id} className="overflow-hidden hover:shadow-md transition-shadow" data-testid={`product-card-${product.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg truncate">{product.name || 'Sem nome'}</CardTitle>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {catName && <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{catName}</span>}
                        {product.sku && <span className="text-xs text-muted-foreground">SKU: {product.sku}</span>}
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => openEditDialog(product)} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded">
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => handleDelete(product.id)} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4 mb-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Custo:</span>
                      <span className="ml-1 font-semibold text-foreground">{formatCurrency(product.cost || 0)}</span>
                    </div>
                  </div>

                  {mpData.length > 0 && (
                    <div className="border-t border-border pt-3">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Marketplace Performance</p>
                      <div className="space-y-3">
                        {mpData.map(mp => (
                          <div key={mp.id} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 p-2.5 rounded-lg bg-muted/50">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: mp.color }} />
                              <span className="font-medium text-sm text-foreground truncate">{mp.name}</span>
                            </div>
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground sm:ml-auto">
                              <span>Preço: <b className="text-foreground">{formatCurrency(mp.sellPrice)}</b></span>
                              <span>Lucro: <b className={mp.profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}>{formatCurrency(mp.profit)}</b></span>
                              <span>Margem: <b className={mp.margin >= 0 ? 'text-emerald-600' : 'text-rose-600'}>{mp.margin.toFixed(1)}%</b></span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        /* List View */
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted">
                <th className="text-left p-3 font-medium text-muted-foreground">Produto</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Custo</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Preços de Venda</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Lucro dos Marketplaces</th>
                <th className="text-right p-3 font-medium text-muted-foreground">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map(product => {
                const mpData = getProductMarketplaceData(product);
                return (
                  <tr key={product.id} className="border-b border-border hover:bg-accent transition-colors" data-testid={`product-row-${product.id}`}>
                    <td className="p-3">
                      <p className="font-medium text-foreground">{product.name || 'Sem nome'}</p>
                      <p className="text-xs text-muted-foreground">{getCategoryName(product.product_category_id)}</p>
                    </td>
                    <td className="p-3 text-foreground whitespace-nowrap">{formatCurrency(product.cost || 0)}</td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1.5">
                        {mpData.map(mp => (
                          <span key={`price-${mp.id}`} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full text-white font-medium"
                            style={{ backgroundColor: mp.color }}>
                            {mp.name}: {formatCurrency(mp.sellPrice)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1.5">
                        {mpData.map(mp => (
                          <span key={`profit-${mp.id}`} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium"
                            style={{ backgroundColor: mp.color + '20', color: mp.color, border: `1px solid ${mp.color}40` }}>
                            {mp.name}: {formatCurrency(mp.profit)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => openEditDialog(product)} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded">
                          <Pencil size={16} />
                        </button>
                        <button onClick={() => handleDelete(product.id)} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Products;
